import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../../services/api';
import { FiPlus, FiEdit, FiTrash2, FiUsers, FiCircle, FiAlertCircle, FiCheckCircle } from 'react-icons/fi';
import '../Students/StudentsPage.css';

const FormulasPage = () => {
    const [formulas, setFormulas] = useState([]);
    const [levels, setLevels] = useState([]);
    const [lessons, setLessons] = useState([]);
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [showProgressModal, setShowProgressModal] = useState(false);
    const [editingFormula, setEditingFormula] = useState(null);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [studentProgress, setStudentProgress] = useState([]);

    const [formData, setFormData] = useState({
        levelId: '',
        lessonId: '',
        formulaEn: '',
        formulaRu: '',
        context: '',
        sortOrder: 0
    });
    
    const [filteredLessons, setFilteredLessons] = useState([]);

    useEffect(() => {
        loadData();
    }, []);
    
    useEffect(() => {
        if (formData.levelId) {
            const filtered = lessons.filter(lesson => {
                return lesson.level_id && lesson.level_id.toString() === formData.levelId.toString();
            });
            
            setFilteredLessons(filtered);
            
            if (formData.lessonId && !filtered.find(l => l.id.toString() === formData.lessonId.toString())) {
                setFormData(prev => ({ ...prev, lessonId: '' }));
            }
        } else {
            setFilteredLessons([]);
            setFormData(prev => ({ ...prev, lessonId: '' }));
        }
    }, [formData.levelId, lessons]);

    const loadData = async () => {
        try {
            setLoading(true);
            const [formulasRes, levelsRes, lessonsRes, studentsRes] = await Promise.all([
                adminAPI.getFormulas(),
                adminAPI.getLevels(),
                adminAPI.getLessons({}),
                adminAPI.getStudents()
            ]);
            
            const formulasData = Array.isArray(formulasRes.data) ? formulasRes.data : (formulasRes.data?.formulas || []);
            const levelsData = Array.isArray(levelsRes.data) ? levelsRes.data : (levelsRes.data?.levels || []);
            const lessonsData = Array.isArray(lessonsRes.data) ? lessonsRes.data : [];
            const studentsData = Array.isArray(studentsRes.data) ? studentsRes.data : (studentsRes.data?.students || []);
            
            setFormulas(formulasData);
            setLevels(levelsData);
            setLessons(lessonsData);
            setStudents(studentsData);
            
            setError('');
        } catch (err) {
            setError('Ошибка при загрузке данных');
            console.error('Load data error:', err);
            
            setFormulas([]);
            setLevels([]);
            setLessons([]);
            setStudents([]);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        try {
            if (editingFormula) {
                await adminAPI.updateFormula(editingFormula.id, formData);
            } else {
                await adminAPI.createFormula(formData);
            }
            
            setShowModal(false);
            setEditingFormula(null);
            resetForm();
            loadData();
        } catch (err) {
            setError(err.response?.data?.error || 'Ошибка при сохранении формулы');
        }
    };

    const handleEdit = (formula) => {
        setEditingFormula(formula);
        setFormData({
            levelId: formula.level_id || '',
            lessonId: formula.lesson_id || '',
            formulaEn: formula.formula_en,
            formulaRu: formula.formula_ru || '',
            context: formula.context || '',
            sortOrder: formula.sort_order || 0
        });
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Вы уверены, что хотите удалить эту формулу?')) {
            return;
        }

        try {
            await adminAPI.deleteFormula(id);
            loadData();
        } catch (err) {
            setError('Ошибка при удалении формулы');
        }
    };

    const handleViewProgress = async (student) => {
        try {
            setSelectedStudent(student);
            const response = await adminAPI.getStudentProgress(student.id);
            setStudentProgress(response.data.progress);
            setShowProgressModal(true);
        } catch (err) {
            setError('Ошибка при загрузке прогресса');
        }
    };

    const handleUpdateProgress = async (formulaId, newStatus) => {
        try {
            await adminAPI.updateStudentProgress(selectedStudent.id, formulaId, { status: newStatus });
            const response = await adminAPI.getStudentProgress(selectedStudent.id);
            setStudentProgress(response.data.progress);
        } catch (err) {
            setError('Ошибка при обновлении прогресса');
        }
    };

    const resetForm = () => {
        setFormData({
            levelId: '',
            lessonId: '',
            formulaEn: '',
            formulaRu: '',
            context: '',
            sortOrder: 0
        });
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingFormula(null);
        resetForm();
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 2:
                return <span style={{ color: '#52c41a', display: 'flex', alignItems: 'center', gap: '5px' }}><FiCheckCircle /> Усвоено</span>;
            case 1:
                return <span style={{ color: '#faad14', display: 'flex', alignItems: 'center', gap: '5px' }}><FiAlertCircle /> Проблема</span>;
            default:
                return <span style={{ color: '#d9d9d9', display: 'flex', alignItems: 'center', gap: '5px' }}><FiCircle /> Не начато</span>;
        }
    };

    if (loading) {
        return <div className="loading">Загрузка...</div>;
    }

    return (
        <div className="container">
            <div className="page-header">
                <div>
                    <h1>Речевые формулы</h1>
                    <p>Управление речевыми формулами и прогрессом учеников</p>
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <button 
                        className="btn btn-secondary"
                        onClick={() => setShowProgressModal(true)}
                    >
                        <FiUsers /> Прогресс учеников
                    </button>
                    <button 
                        className="btn btn-primary"
                        onClick={() => setShowModal(true)}
                    >
                        <FiPlus /> Добавить формулу
                    </button>
                </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="card">
                <div className="students-table">
                    <table>
                        <thead>
                            <tr>
                                <th>№</th>
                                <th>Формула (EN)</th>
                                <th>Перевод (RU)</th>
                                <th>Контекст</th>
                                <th>Уровень</th>
                                <th>Урок</th>
                                <th>Статус</th>
                                <th>Действия</th>
                            </tr>
                        </thead>
                        <tbody>
                            {formulas.length === 0 ? (
                                <tr>
                                    <td colSpan="8" style={{ textAlign: 'center' }}>
                                        Нет формул. Создайте первую формулу.
                                    </td>
                                </tr>
                            ) : (
                                formulas.map((formula) => (
                                    <tr key={formula.id}>
                                        <td>{formula.sort_order}</td>
                                        <td><strong>{formula.formula_en}</strong></td>
                                        <td>{formula.formula_ru || '—'}</td>
                                        <td style={{ fontSize: '12px', color: '#666' }}>
                                            {formula.context || '—'}
                                        </td>
                                        <td>{formula.level_name || '—'}</td>
                                        <td>{formula.lesson_title || '—'}</td>
                                        <td>
                                            <span className={`status-badge ${formula.is_active ? 'active' : 'inactive'}`}>
                                                {formula.is_active ? 'Активна' : 'Неактивна'}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="table-actions">
                                                <button
                                                    className="btn-icon"
                                                    onClick={() => handleEdit(formula)}
                                                    title="Редактировать"
                                                >
                                                    <FiEdit />
                                                </button>
                                                <button
                                                    className="btn-icon btn-danger"
                                                    onClick={() => handleDelete(formula.id)}
                                                    title="Удалить"
                                                >
                                                    <FiTrash2 />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {showModal && (
                <div className="modal-overlay" onClick={handleCloseModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{editingFormula ? 'Редактировать формулу' : 'Добавить формулу'}</h2>
                            <button onClick={handleCloseModal} className="modal-close">×</button>
                        </div>

                        <form onSubmit={handleSubmit} className="modal-form">
                            <div className="form-group">
                                <label>Формула на английском *</label>
                                <input
                                    type="text"
                                    name="formulaEn"
                                    value={formData.formulaEn}
                                    onChange={handleInputChange}
                                    placeholder="Например: How are you?"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label>Перевод на русский</label>
                                <input
                                    type="text"
                                    name="formulaRu"
                                    value={formData.formulaRu}
                                    onChange={handleInputChange}
                                    placeholder="Например: Как дела?"
                                />
                            </div>

                            <div className="form-group">
                                <label>Контекст использования</label>
                                <textarea
                                    name="context"
                                    value={formData.context}
                                    onChange={handleInputChange}
                                    placeholder="Когда и как использовать эту формулу"
                                    rows="3"
                                />
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>Уровень (опционально)</label>
                                    <select
                                        name="levelId"
                                        value={formData.levelId}
                                        onChange={handleInputChange}
                                    >
                                        <option value="">Не привязывать к уровню</option>
                                        {levels && levels.length > 0 && levels.map(level => (
                                            <option key={level.id} value={level.id}>
                                                {level.area_name ? `${level.area_name} - ${level.name}` : level.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>Урок (опционально)</label>
                                    <select
                                        name="lessonId"
                                        value={formData.lessonId}
                                        onChange={handleInputChange}
                                        disabled={!formData.levelId}
                                    >
                                        <option value="">
                                            {formData.levelId ? 'Не привязывать к уроку' : 'Сначала выберите уровень'}
                                        </option>
                                        {filteredLessons && filteredLessons.length > 0 && filteredLessons.map(lesson => (
                                            <option key={lesson.id} value={lesson.id}>
                                                {lesson.title}
                                            </option>
                                        ))}
                                    </select>
                                    {formData.levelId && filteredLessons.length === 0 && (
                                        <small style={{ color: '#999', marginTop: '5px', display: 'block' }}>
                                            Нет уроков для выбранного уровня
                                        </small>
                                    )}
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Порядок сортировки</label>
                                <input
                                    type="number"
                                    name="sortOrder"
                                    value={formData.sortOrder}
                                    onChange={handleInputChange}
                                    min="0"
                                />
                            </div>

                            <div className="modal-footer">
                                <button type="button" className="btn" onClick={handleCloseModal}>
                                    Отмена
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    {editingFormula ? 'Сохранить' : 'Создать'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {showProgressModal && !selectedStudent && (
                <div className="modal-overlay" onClick={() => setShowProgressModal(false)}>
                    <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Выберите ученика</h2>
                            <button onClick={() => setShowProgressModal(false)} className="modal-close">×</button>
                        </div>

                        <div className="modal-form">
                            <div style={{ display: 'grid', gap: '10px' }}>
                                {students.map(student => (
                                    <div 
                                        key={student.id}
                                        className="card"
                                        style={{ padding: '15px', cursor: 'pointer', transition: 'all 0.3s' }}
                                        onClick={() => handleViewProgress(student)}
                                        onMouseEnter={(e) => e.currentTarget.style.background = '#f0f7ff'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                                    >
                                        <strong>{student.first_name} {student.last_name}</strong>
                                        <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                                            {student.username}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showProgressModal && selectedStudent && (
                <div className="modal-overlay" onClick={() => {
                    setShowProgressModal(false);
                    setSelectedStudent(null);
                }}>
                    <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <div>
                                <h2>Прогресс: {selectedStudent.first_name} {selectedStudent.last_name}</h2>
                                <button 
                                    onClick={() => setSelectedStudent(null)}
                                    style={{ 
                                        marginTop: '5px', 
                                        padding: '4px 12px', 
                                        fontSize: '12px',
                                        background: '#f0f0f0',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    ← Назад к списку
                                </button>
                            </div>
                            <button onClick={() => {
                                setShowProgressModal(false);
                                setSelectedStudent(null);
                            }} className="modal-close">×</button>
                        </div>

                        <div className="modal-form" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                            {studentProgress.map(item => (
                                <div key={item.id} className="card" style={{ padding: '15px', marginBottom: '12px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '15px' }}>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontWeight: '500', marginBottom: '5px' }}>
                                                {item.formula_en}
                                            </div>
                                            {item.formula_ru && (
                                                <div style={{ fontSize: '14px', color: '#7f8c8d', fontStyle: 'italic' }}>
                                                    {item.formula_ru}
                                                </div>
                                            )}
                                        </div>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <button
                                                onClick={() => handleUpdateProgress(item.id, 0)}
                                                style={{
                                                    padding: '6px 12px',
                                                    border: item.status === 0 || !item.status ? '2px solid #d9d9d9' : '1px solid #e8e8e8',
                                                    background: item.status === 0 || !item.status ? '#f5f5f5' : 'white',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontSize: '14px',
                                                    display: 'flex',
                                                    alignItems: 'center'
                                                }}
                                                title="Не начато"
                                            >
                                                <FiCircle color="#d9d9d9" />
                                            </button>
                                            <button
                                                onClick={() => handleUpdateProgress(item.id, 1)}
                                                style={{
                                                    padding: '6px 12px',
                                                    border: item.status === 1 ? '2px solid #faad14' : '1px solid #e8e8e8',
                                                    background: item.status === 1 ? '#fffbe6' : 'white',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontSize: '14px',
                                                    display: 'flex',
                                                    alignItems: 'center'
                                                }}
                                                title="Есть проблема"
                                            >
                                                <FiAlertCircle color="#faad14" />
                                            </button>
                                            <button
                                                onClick={() => handleUpdateProgress(item.id, 2)}
                                                style={{
                                                    padding: '6px 12px',
                                                    border: item.status === 2 ? '2px solid #52c41a' : '1px solid #e8e8e8',
                                                    background: item.status === 2 ? '#f6ffed' : 'white',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontSize: '14px',
                                                    display: 'flex',
                                                    alignItems: 'center'
                                                }}
                                                title="Усвоено"
                                            >
                                                <FiCheckCircle color="#52c41a" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                .modal-large {
                    max-width: 700px;
                }
                .status-badge {
                    padding: 4px 12px;
                    border-radius: 12px;
                    font-size: 12px;
                    font-weight: 500;
                }
                .status-badge.active {
                    background-color: #f6ffed;
                    color: #52c41a;
                    border: 1px solid #b7eb8f;
                }
                .status-badge.inactive {
                    background-color: #fff2f0;
                    color: #f5222d;
                    border: 1px solid #ffccc7;
                }
            `}</style>
        </div>
    );
};

export default FormulasPage;
